// src/pages/business/posts/PostOffer.tsx
// Web adaptation of mobile PostOfferScreen - Create special offers and promotions

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Image, Video, Tag, Clock, DollarSign,
  Loader2, XCircle, CheckCircle, Calendar, Users, TrendingUp
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
}

interface Service {
  id: string;
  name: string;
  price: number;
}

const PostOffer: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [products, setProducts] = useState<Product[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Form state
  const [offerType, setOfferType] = useState<'discount' | 'buy_x_get_y' | 'special_price'>('discount');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [buyQuantity, setBuyQuantity] = useState('1');
  const [getQuantity, setGetQuantity] = useState('1');
  const [specialPrice, setSpecialPrice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [minPurchase, setMinPurchase] = useState('');

  useEffect(() => {
    fetchBusinessData();
  }, [businessId]);

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      const [productsRes, servicesRes] = await Promise.all([
        apiClient.get(`/businesses/${businessId}/products`),
        apiClient.get(`/businesses/${businessId}/services`)
      ]);
      setProducts(productsRes.data.products || []);
      setServices(servicesRes.data.services || []);
    } catch (error: any) {
      console.error('Error fetching business data:', error);
      toastError('Failed to load business data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      toastError('Please enter an offer title');
      return;
    }
    if (selectedProducts.length === 0 && selectedServices.length === 0) {
      toastError('Please select at least one product or service');
      return;
    }
    if (!endDate) {
      toastError('Please set an end date');
      return;
    }

    setCreating(true);
    try {
      const offerData: any = {
        title: title.trim(),
        description: description.trim(),
        type: offerType,
        product_ids: selectedProducts,
        service_ids: selectedServices,
        end_date: endDate,
        max_uses: maxUses ? parseInt(maxUses) : null,
        min_purchase: minPurchase ? parseFloat(minPurchase) : null,
      };

      if (offerType === 'discount') {
        offerData.discount_type = discountType;
        offerData.discount_value = parseFloat(discountValue);
      } else if (offerType === 'buy_x_get_y') {
        offerData.buy_quantity = parseInt(buyQuantity);
        offerData.get_quantity = parseInt(getQuantity);
      } else if (offerType === 'special_price') {
        offerData.special_price = parseFloat(specialPrice);
      }

      if (startDate) {
        offerData.start_date = startDate;
      }

      await apiClient.post(`/businesses/${businessId}/offers`, offerData);
      toastSuccess('Offer created successfully');
      navigate(`/business/${businessId}/posts`);
    } catch (error: any) {
      toastError('Failed to create offer');
    } finally {
      setCreating(false);
    }
  };

  const toggleProduct = (productId: string) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleService = (serviceId: string) => {
    setSelectedServices(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}/posts`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Create Offer
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}/posts`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Create Offer
        </h1>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Offer Type Selection */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
          <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Offer Type</h2>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => setOfferType('discount')}
              className={`p-4 rounded-xl border-2 transition-all ${
                offerType === 'discount'
                  ? 'border-primary bg-primary/5'
                  : 'border-light-border dark:border-dark-border hover:border-primary/50'
              }`}
            >
              <Tag size={24} className={`mx-auto mb-2 ${offerType === 'discount' ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`} />
              <p className={`text-sm font-medium ${offerType === 'discount' ? 'text-primary' : 'text-light-text dark:text-dark-text'}`}>
                Discount
              </p>
            </button>
            <button
              onClick={() => setOfferType('buy_x_get_y')}
              className={`p-4 rounded-xl border-2 transition-all ${
                offerType === 'buy_x_get_y'
                  ? 'border-primary bg-primary/5'
                  : 'border-light-border dark:border-dark-border hover:border-primary/50'
              }`}
            >
              <Users size={24} className={`mx-auto mb-2 ${offerType === 'buy_x_get_y' ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`} />
              <p className={`text-sm font-medium ${offerType === 'buy_x_get_y' ? 'text-primary' : 'text-light-text dark:text-dark-text'}`}>
                Buy X Get Y
              </p>
            </button>
            <button
              onClick={() => setOfferType('special_price')}
              className={`p-4 rounded-xl border-2 transition-all ${
                offerType === 'special_price'
                  ? 'border-primary bg-primary/5'
                  : 'border-light-border dark:border-dark-border hover:border-primary/50'
              }`}
            >
              <DollarSign size={24} className={`mx-auto mb-2 ${offerType === 'special_price' ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`} />
              <p className={`text-sm font-medium ${offerType === 'special_price' ? 'text-primary' : 'text-light-text dark:text-dark-text'}`}>
                Special Price
              </p>
            </button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
          <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Offer Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Offer Title
              </label>
              <input
                type="text"
                placeholder="e.g., Summer Sale, Weekend Special"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your offer..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Discount Configuration */}
        {offerType === 'discount' && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
            <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Discount Settings</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                    Discount Type
                  </label>
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed')}
                    className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (KES)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                    Discount Value
                  </label>
                  <input
                    type="number"
                    placeholder={discountType === 'percentage' ? 'e.g., 20' : 'e.g., 500'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Buy X Get Y Configuration */}
        {offerType === 'buy_x_get_y' && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
            <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Buy X Get Y Settings</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                  Buy Quantity
                </label>
                <input
                  type="number"
                  placeholder="1"
                  value={buyQuantity}
                  onChange={(e) => setBuyQuantity(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                  Get Quantity
                </label>
                <input
                  type="number"
                  placeholder="1"
                  value={getQuantity}
                  onChange={(e) => setGetQuantity(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </div>
        )}

        {/* Special Price Configuration */}
        {offerType === 'special_price' && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
            <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Special Price</h2>
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Special Price (KES)
              </label>
              <input
                type="number"
                placeholder="e.g., 1500"
                value={specialPrice}
                onChange={(e) => setSpecialPrice(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
        )}

        {/* Product/Service Selection */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
          <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Apply To</h2>

          {/* Products */}
          {products.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-light-text dark:text-dark-text mb-3">Products</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {products.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => toggleProduct(product.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                      selectedProducts.includes(product.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-light-border dark:border-dark-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded flex items-center justify-center ${
                        selectedProducts.includes(product.id) ? 'bg-primary' : 'border border-light-border dark:border-dark-border'
                      }`}>
                        {selectedProducts.includes(product.id) && <CheckCircle size={14} className="text-white" />}
                      </div>
                      <span className="text-sm text-light-text dark:text-dark-text">{product.name}</span>
                    </div>
                    <span className="text-sm font-medium text-light-subtle dark:text-dark-subtle">
                      KES {product.price.toLocaleString()}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Services */}
          {services.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-light-text dark:text-dark-text mb-3">Services</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => toggleService(service.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                      selectedServices.includes(service.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-light-border dark:border-dark-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded flex items-center justify-center ${
                        selectedServices.includes(service.id) ? 'bg-primary' : 'border border-light-border dark:border-dark-border'
                      }`}>
                        {selectedServices.includes(service.id) && <CheckCircle size={14} className="text-white" />}
                      </div>
                      <span className="text-sm text-light-text dark:text-dark-text">{service.name}</span>
                    </div>
                    <span className="text-sm font-medium text-light-subtle dark:text-dark-subtle">
                      KES {service.price.toLocaleString()}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Schedule */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
          <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Schedule</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Start Date (Optional)
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                End Date *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
        </div>

        {/* Additional Settings */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
          <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Additional Settings</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Max Uses (Optional)
              </label>
              <input
                type="number"
                placeholder="e.g., 100"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Min Purchase (Optional)
              </label>
              <input
                type="number"
                placeholder="e.g., 1000"
                value={minPurchase}
                onChange={(e) => setMinPurchase(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={creating}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-white font-semibold text-lg transition-all hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#4F46E5' }}
        >
          {creating ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Creating Offer...
            </>
          ) : (
            <>
              <TrendingUp size={20} />
              Create Offer
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PostOffer;